import {
  combineEpics,
  type Epic,
  type StateObservable,
} from 'redux-observable';
import {
  filter,
  from,
  mergeMap,
  map,
  catchError,
  of,
  merge,
  ignoreElements,
  withLatestFrom,
  Observable,
  EMPTY,
  switchMap,
  takeUntil,
} from 'rxjs';
import { initPusher } from '@/core/services/pusher';
import {
  listAdminItems,
  getAdminItemsOpenCount,
  resolveAdminItem,
  archiveAdminItem,
} from '@/api/istaff/adminItems';
import { setAuth, setUser, clearAuth } from '@/slices/auth/actions';
import { selectAccessToken, selectUser } from '@/slices/auth/selectors';
import { resolveSidebarRole } from '@/components/Sidebar';
import type { RootState } from '@/state/store';
import {
  loadAdminItems,
  syncOpenCount,
  resolveItem,
  archiveItem,
  setLoading,
  setAdminItemsStatus,
  setAdminItemsPage,
  setOpenCount,
  removeItem,
} from '../actions';
import {
  selectAdminItemsPage,
  selectAdminItemsPerPage,
  selectAdminItemsStatus,
} from '../selectors';

const loadAdminItemsEpic: Epic = (action$) =>
  action$.pipe(
    filter(loadAdminItems.match),
    mergeMap(({ payload }) => {
      const status = payload?.status ?? 'open';
      return merge(
        payload?.silent ? EMPTY : of(setLoading(true)),
        of(setAdminItemsStatus(status)),
        from(
          listAdminItems({
            page: payload?.page ?? 1,
            perPage: payload?.perPage,
            status,
          }),
        ).pipe(
          map((res) => setAdminItemsPage(res)),
          catchError(() => of(setLoading(false))),
        ),
      );
    }),
  );

const syncOpenCountEpic: Epic = (action$) =>
  action$.pipe(
    filter(syncOpenCount.match),
    mergeMap(() =>
      from(getAdminItemsOpenCount()).pipe(
        map((res) => setOpenCount(res.open_count)),
        catchError(() => EMPTY),
      ),
    ),
  );

const resolveItemEpic: Epic = (action$, state$) =>
  action$.pipe(
    filter(resolveItem.match),
    withLatestFrom(state$ as Observable<RootState>),
    mergeMap(([{ payload: id }, state]) =>
      merge(
        of(removeItem(id)),
        from(resolveAdminItem(id)).pipe(
          ignoreElements(),
          catchError(() =>
            of(
              loadAdminItems({
                page: 1,
                status: selectAdminItemsStatus(state),
                perPage: selectAdminItemsPerPage(state),
              }),
            ),
          ),
        ),
      ).pipe(mergeMap((action) => of(action, syncOpenCount()))),
    ),
  );

const archiveItemEpic: Epic = (action$, state$) =>
  action$.pipe(
    filter(archiveItem.match),
    withLatestFrom(state$ as Observable<RootState>),
    mergeMap(([{ payload: id }, state]) =>
      merge(
        of(removeItem(id)),
        from(archiveAdminItem(id)).pipe(
          ignoreElements(),
          catchError(() =>
            of(
              loadAdminItems({
                page: 1,
                status: selectAdminItemsStatus(state),
                perPage: selectAdminItemsPerPage(state),
              }),
            ),
          ),
        ),
      ).pipe(mergeMap((action) => of(action, syncOpenCount()))),
    ),
  );

const ADMIN_ITEM_EVENT = '.admin-item.changed';

const pusherEpic: Epic = (action$, state$) => {
  const typedState$ = state$ as StateObservable<RootState>;

  return merge(
    action$.pipe(
      filter(setAuth.match),
      map(({ payload }) => payload.accessToken),
    ),
    action$.pipe(
      filter(setUser.match),
      withLatestFrom(typedState$),
      map(([, state]) => selectAccessToken(state)),
    ),
  ).pipe(
    withLatestFrom(typedState$),
    switchMap(([token, state]) => {
      const user = selectUser(state);
      const role = resolveSidebarRole({
        roles: user?.roles,
        type: user?.type,
      });
      if (!token || role !== 'admin') return EMPTY;

      const pusher = initPusher(token);
      const channel = pusher.subscribe('private-admin-items');

      return new Observable<
        ReturnType<typeof syncOpenCount> | ReturnType<typeof loadAdminItems>
      >((observer) => {
        channel.bind(ADMIN_ITEM_EVENT, () => {
          // Per the API guide: treat `.admin-item.changed` as an
          // invalidation signal and refetch the open count plus the
          // visible inbox page. `state` above is a one-time snapshot from
          // when the Pusher connection was set up (login), so read
          // `typedState$.value` here instead to get the admin's current
          // status filter and page at the moment the event actually fires.
          const currentState = typedState$.value;
          observer.next(syncOpenCount());
          observer.next(
            loadAdminItems({
              page: selectAdminItemsPage(currentState),
              status: selectAdminItemsStatus(currentState),
              perPage: selectAdminItemsPerPage(currentState),
              silent: true,
            }),
          );
        });
        return () => {
          channel.unbind_all();
          pusher.unsubscribe('private-admin-items');
        };
      }).pipe(takeUntil(action$.pipe(filter(clearAuth.match))));
    }),
  );
};

export default combineEpics(
  loadAdminItemsEpic,
  syncOpenCountEpic,
  resolveItemEpic,
  archiveItemEpic,
  pusherEpic,
);
